// src/App.js
import React, { useEffect, useState, memo } from "react";
import { joinRoom, socket } from "./utils/socket";
import { getQueue, getRooms, getUserPreferences, saveUserPreferences } from "./api";
import QueueList from "./components/QueueList";
import SearchBar from "./components/SearchBar";
import PlayerPage from "./components/PlayerInstructions";
import Login from "./components/Login";
import ManageRooms from "./components/ManageRooms";
import { BrowserRouter as Router, Routes, Route, Link } from "react-router-dom";
import {
  Box, Typography, IconButton, Menu, MenuItem, Divider,
  FormControl, InputLabel, Select, MenuItem as SelectItem
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import './App.css';

const MainContent = memo(function MainContent({ queue, loadingQueue, isMobile, roomId }) {
  return (
    <Box sx={{
      display: "flex",
      flexDirection: isMobile ? "column" : "row",
      height: "calc(100vh - 160px)",
      overflow: "hidden"
    }}>
      {/* Queue List */}
      <Box sx={{
        width: isMobile ? "100%" : "40%",
        p: 2,
        overflowY: "auto",
        flex: isMobile ? "0 0 40%" : "0 0 40%",   // 45% height on mobile
        borderBottom: isMobile ? "1px solid #ccc" : "none",
        borderRight: isMobile ? "none" : "1px solid #ccc"
      }}>
        {loadingQueue ? (
          <p>Loading queue…</p>
        ) : (
          <QueueList queue={queue} room={roomId} />
        )}
      </Box>

      {/* Search Bar */}
      <Box sx={{
        width: isMobile ? "100%" : "60%",
        p: 2,
        overflowY: "auto",
        flex: isMobile ? "1 1 auto" : "0 0 60%"     // takes remaining space on mobile
      }}>
        <SearchBar room={roomId} />
      </Box>
    </Box>
  );
});

function App() {
  const [queue, setQueue] = useState({ songs: [] });
  // const [roomId, setRoomId] = useState("default");
  const [roomId, setRoomId] = useState(() => {
    // Restore last selected room on page load/refresh
    return sessionStorage.getItem("lastRoom") || "default";
  });
  const [rooms, setRooms] = useState(["default"]);
  const [loadingQueue, setLoadingQueue] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 900); // Better breakpoint
  const [userName, setUserName] = useState("");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [headerText, setHeaderText] = useState("The Home Karaoke Lounge");
  const [loadingRooms, setLoadingRooms] = useState(false);

  // Restore last room from server after login
  useEffect(() => {
    if (isLoggedIn) {
      getUserPreferences().then(data => {
        const serverRoom = data.lastRoom;
        setRooms(rooms);
        setRoomId(serverRoom);
        sessionStorage.setItem("lastRoom", serverRoom);
      }).catch(() => {
        setRoomId("default");
      });
    }
  }, [isLoggedIn]);

  // Load rooms only after login + token is ready
  const loadRooms = async () => {
    setLoadingRooms(true);
    try {
      const data = await getRooms();
      const newRooms = data.rooms || ["default"];
      setRooms(newRooms);

      // Validate that the previously saved room still exists
      const savedRoom = sessionStorage.getItem("lastRoom");
      if (savedRoom && newRooms.includes(savedRoom)) {
        setRoomId(savedRoom);
      } else {
        setRoomId("default");
        sessionStorage.setItem("lastRoom", "default");
      }
      // if (!newRooms.includes(roomId)) {
      //   setRoomId("default");
      // }
    } catch (e) {
      console.error("Failed to load rooms:", e);
      setRooms(["default"]);
      setRoomId("default");
      sessionStorage.setItem("lastRoom", "default");
    } finally {
      setLoadingRooms(false);
    }
  };

  // Load rooms when user logs in or token changes
  useEffect(() => {
    if (isLoggedIn) {
      loadRooms();
    }
  }, [isLoggedIn]);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 900);
    };
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  // Real-time room updates
  useEffect(() => {
    loadRooms();
    socket.on("rooms:update", loadRooms);
    return () => socket.off("rooms:update", loadRooms);
  }, []);

  // Join room & load queue
  useEffect(() => {
    if (!isLoggedIn) return;
    joinRoom(roomId, userName, () => {
      getQueue(roomId).then((data) => {
        setQueue(data || { songs: [] });
        setLoadingQueue(false);
      }).catch(() => {
        setQueue({ songs: [] });
        setLoadingQueue(false);
      });
    });
  }, [roomId, userName, isLoggedIn]);

  // NEW: Refresh queue when page becomes visible again (mobile standby fix)
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === "visible" && isLoggedIn && roomId) {
        console.log("📱 Page became visible - refreshing queue from server");

        setLoadingQueue(true);   // Optional: shows "Loading..." briefly

        try {
          const freshQueue = await getQueue(roomId);
          setQueue(freshQueue || { songs: [] });
        } catch (err) {
          console.error("Failed to refresh queue:", err);
        } finally {
          setLoadingQueue(false);
        }

        // Re-join room to make sure socket is active again
        joinRoom(roomId, userName);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleVisibilityChange);   // extra safety

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleVisibilityChange);
    };
  }, [isLoggedIn, roomId, userName]);   // dependencies are important

  useEffect(() => {
    const handleQueueUpdate = (newQueue) => {
      if (newQueue.room === roomId) setQueue(newQueue);
    };
    socket.on("queue:update", handleQueueUpdate);
    return () => socket.off("queue:update", handleQueueUpdate);
  }, [roomId]);

  useEffect(() => {
    const savedUser = sessionStorage.getItem("userName");
    if (savedUser) {
      setUserName(savedUser);
      setIsLoggedIn(true);
    }
  }, []);

  const handleLogin = (username) => {
    setUserName(username);
    setIsLoggedIn(true);
  };

  const handleRoomChange = (e) => {
    // setRoomId(e.target.value);
    const newRoom = e.target.value;
    setRoomId(newRoom);
    sessionStorage.setItem("lastRoom", newRoom); 
    setLoadingQueue(true);
    saveUserPreferences(newRoom).catch(err => console.error("Failed to save lastRoom:", err));
  };

  const handleLogout = () => {
    // Clear JWT token
    sessionStorage.removeItem("token");
    
    // Clear saved username and room
    sessionStorage.removeItem("userName");
    sessionStorage.removeItem("lastRoom");

    // Clear Socket.io token and disconnect
    if (typeof updateSocketToken === "function") {
      updateSocketToken(null)
    }
    
    // Force socket disconnection
    if (socket && typeof socket.disconnect === "function") {
      socket.disconnect();
    }

    // Reload the page to go back to login screen
    window.location.reload();
  };

  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const updateHeaderText = (newText) => setHeaderText(newText);

  if (!isLoggedIn) return <Login onLogin={handleLogin} />;

  return (
    <Router>
      <div className="App app-background">
        <Box sx={{ p: 1, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
          <IconButton edge="start" color="inherit" onClick={handleMenuOpen}>
            <MenuIcon />
          </IconButton>

          <Typography variant="h5">{headerText}</Typography>

          {/* Room Selector */}
          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel>Room</InputLabel>
            <Select value={roomId} label="Room" onChange={handleRoomChange} disabled={loadingRooms}>
              {rooms.map((r) => (
                <SelectItem key={r} value={r}>{r}</SelectItem>
              ))}
            </Select>
          </FormControl>

          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
            <MenuItem component={Link} to="/" onClick={handleMenuClose}>Song Search</MenuItem>
            <MenuItem component={Link} to="/manage-rooms" onClick={handleMenuClose}>Manage Rooms</MenuItem>

            <Divider sx={{ my: 1 }} />

            <MenuItem component={Link} to="/player" onClick={handleMenuClose}>Set Up Player</MenuItem>

            <Divider sx={{ my: 1 }} />

            <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
              Logout ({userName})
            </MenuItem>
          </Menu>
        </Box>

        <Routes>
          <Route path="/" element={<MainContent queue={queue} loadingQueue={loadingQueue} isMobile={isMobile} roomId={roomId} />} />
          <Route path="/player" element={<PlayerPage headerUpdater={updateHeaderText} room={roomId} />} />
          <Route path="/manage-rooms" element={<ManageRooms />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;